'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  FileText,
  X,
  Upload,
  Camera,
  Trash2,
  Download,
  Eye,
  ChevronRight,
  FolderOpen,
  Receipt,
  Image as ImageIcon,
  File,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { notify, confirm } from '@/lib/notifications';
import { useCamera, SimpleCameraModal } from '@/hooks/useCamera';
import { isCapacitor } from '@/lib/platformUtils';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

interface CustomEvent<T> extends Event {
  detail: T;
}

const SUPPORTING_DOCUMENTS_OVERLAY_Z_CLASS = 'z-[230]';
const SUPPORTING_DOCUMENTS_PANEL_Z_CLASS = 'z-[231]';

interface SupportingDocument {
  id: string;
  file_name: string;
  file_url: string;
  file_type: string;
  file_size: number;
  uploaded_at: string;
  document_type?: 'receipt' | 'invoice' | 'quote' | 'other' | 'delivery_note' | 'supplier_invoice';
  ai_validated?: boolean;
  ai_confidence?: number;
  source?: 'field_worker' | 'admin' | 'system';
  field_worker_id?: number;
  field_worker_name?: string;
  linked_purchase_order_id?: string;
  linked_purchase_order_number?: string;
  is_partial_delivery?: boolean;
  delivery_status?: string;
}

interface ProcurementDocumentResponse {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  uploaded_by: number;
  uploaded_by_name: string;
  created_at: string;
  metadata: {
    document_type?: string;
    source?: string;
    linked_purchase_order_id?: string;
    linked_purchase_order_number?: string;
    is_partial_delivery?: boolean;
    delivery_status?: string;
    ai_validated?: boolean;
    ai_confidence?: number;
  };
}

interface WorkItemPhoto {
  id: string;
  photo_url: string;
  photo_type: string;
  caption: string;
  ai_validated: boolean;
  ai_confidence: number;
  ai_vendor_name: string;
  ai_detected_amount: number;
  ai_validation_match: string;
  ai_analysis_result: Record<string, unknown>;
  captured_by_user_id: number;
  captured_by_name: string;
  created_at: string;
}

// ============================================================================
// PHOTO VIEWER COMPONENT
// ============================================================================

interface PhotoViewerProps {
  photo: SupportingDocument;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onDelete?: (photoId: string) => void;
}

function PhotoViewer({ photo, onClose, onNext, onPrevious, onDelete }: PhotoViewerProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.mode === 'dark';
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 4));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 1));
  const handleResetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) handleZoomIn();
    else handleZoomOut();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && onNext) onNext();
      if (e.key === 'ArrowLeft' && onPrevious) onPrevious();
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-' || e.key === '_') handleZoomOut();
      if (e.key === '0') handleResetZoom();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrevious]);

  return (
    <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4">
      {/* Close */}
      <button onClick={onClose} className="absolute top-4 right-4 p-3 bg-red-600 hover:bg-red-700 rounded-full text-white transition-colors shadow-lg z-10" title="Close (Esc)">
        <X className="w-6 h-6" />
      </button>

      {/* Zoom Controls */}
      <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
        <button onClick={handleZoomOut} disabled={zoom <= 1} className="p-3 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-full text-white transition-colors shadow-lg border border-gray-600" title="Zoom Out (-)">
          <ZoomOut className="w-5 h-5" />
        </button>
        <span className="px-3 py-1 bg-gray-800 text-white text-sm font-mono rounded-lg border border-gray-600 min-w-[3rem] text-center">
          {Math.round(zoom * 100)}%
        </span>
        <button onClick={handleZoomIn} disabled={zoom >= 4} className="p-3 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-full text-white transition-colors shadow-lg border border-gray-600" title="Zoom In (+)">
          <ZoomIn className="w-5 h-5" />
        </button>
        <button onClick={handleResetZoom} disabled={zoom === 1} className="p-3 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-full text-white transition-colors shadow-lg border border-gray-600" title="Reset Zoom (0)">
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>

      {/* Nav Left */}
      {onPrevious && (
        <button onClick={onPrevious} className="absolute left-4 top-1/2 -translate-y-1/2 p-4 bg-gray-800 hover:bg-gray-700 rounded-full text-white transition-colors shadow-lg border border-gray-600 z-10" title="Previous (←)">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>
      )}

      {/* Nav Right */}
      {onNext && (
        <button onClick={onNext} className="absolute right-4 top-1/2 -translate-y-1/2 p-4 bg-gray-800 hover:bg-gray-700 rounded-full text-white transition-colors shadow-lg border border-gray-600 z-10" title="Next (→)">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      )}

      {/* Image & Info */}
      <div className="max-w-7xl w-full flex flex-col items-center px-4">
        <div
          ref={containerRef}
          className="relative overflow-hidden rounded-lg shadow-2xl cursor-grab active:cursor-grabbing"
          style={{ maxHeight: '65vh', maxWidth: '100%' }}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <img
            ref={imageRef}
            src={photo.file_url}
            alt={photo.file_name}
            className="max-h-[65vh] max-w-full object-contain transition-transform duration-75 ease-out"
            style={{
              transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              cursor: zoom > 1 ? 'grab' : 'default',
            }}
            draggable={false}
          />
        </div>

        <div className="mt-4 bg-gray-900 border border-gray-700 rounded-xl p-5 text-white w-full max-w-3xl shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <span className="text-3xl">📄</span>
              <div>
                <span className="font-bold text-lg">{photo.file_name}</span>
                {photo.ai_validated && (
                  <div className="flex items-center gap-1 mt-1">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-xs text-green-500">AI Validated ({Math.round((photo.ai_confidence || 0) * 100)}%)</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a href={photo.file_url} download={photo.file_name} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium">
                <Download className="w-4 h-4" /> Download
              </a>
              {onDelete && (
                <button
                  onClick={() => onDelete(photo.id)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-gray-700/60 border border-gray-500 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Uploaded:</span>
                <span className="ml-2 text-white">{new Date(photo.uploaded_at).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-400">Type:</span>
                <span className="ml-2 text-white">{photo.document_type || 'Document'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface SupportingDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workItemId: string;
}

type ViewMode = 'upload' | 'gallery' | 'ai-analysis';

export default function SupportingDocumentsModal({
  isOpen,
  onClose,
  workItemId,
}: SupportingDocumentsModalProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.mode === 'dark';
  const [mounted, setMounted] = useState(false);
  const [activeView, setActiveView] = useState<ViewMode>('gallery');
  
  // Document state
  const [documents, setDocuments] = useState<SupportingDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<SupportingDocument | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<SupportingDocument | null>(null);
  
  // Upload state
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const getAuthToken = useCallback(() => {
    try {
      const token = localStorage.getItem('auth_tokens');
      const tokens = token ? JSON.parse(token) : null;
      return tokens?.access_token || '';
    } catch {
      return '';
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      if (!token) return;

      // Load attachments from work_item_attachments table
      const response = await fetch(`/api/v1/work-items/${workItemId}/attachments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let loadedDocuments: SupportingDocument[] = [];
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          // Map work_item_attachments to SupportingDocument format.
          // The attachments endpoint returns file_path (cloud-storage URL)
          // and download_url; SupportingDocument uses file_url for rendering.
          loadedDocuments = data.data.map((att: Record<string, unknown>) => ({
            id: String(att.id ?? ''),
            file_name: String(att.file_name ?? 'Attachment'),
            file_url: String(att.download_url ?? att.file_path ?? ''),
            file_type: String(att.file_type ?? 'image/jpeg'),
            file_size: Number(att.file_size ?? 0),
            uploaded_at: String(att.created_at ?? new Date().toISOString()),
            document_type: (att.category === 'receipt' ? 'receipt' : 'other') as SupportingDocument['document_type'],
          }));
        }
      }

      // Also load photos from work_item_photos table (for expenses created via ChildEntryForm)
      const photosResponse = await fetch(`/api/v1/work-items/photos?work_item_id=${workItemId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (photosResponse.ok) {
        const photosData = await photosResponse.json();
        if (photosData.success && Array.isArray(photosData.data)) {
          // Convert photos to SupportingDocument format
          const photoDocuments: SupportingDocument[] = photosData.data.map((photo: WorkItemPhoto) => ({
            id: photo.id,
            file_name: photo.caption || photo.photo_type || 'Receipt Photo',
            file_url: photo.photo_url,
            file_type: 'image/jpeg',
            file_size: 0,
            uploaded_at: photo.created_at,
            document_type: 'receipt',
            ai_validated: photo.ai_validated,
            ai_confidence: photo.ai_confidence,
          }));
          loadedDocuments = [...loadedDocuments, ...photoDocuments];
        }
      }

      // Fetch the work item to check if it's a purchase order (for procurement documents)
      const workItemResponse = await fetch(`/api/v1/work-items/${workItemId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (workItemResponse.ok) {
        const workItemData = await workItemResponse.json();
        if (workItemData.success && workItemData.data) {
          // Load procurement documents if this is a purchase order
          if (workItemData.data.item_type === 'purchase_order') {
            try {
              const procurementResponse = await fetch(`/api/v1/procurement?action=purchase-order-documents&purchase_order_id=${workItemId}`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });

              if (procurementResponse.ok) {
                const procurementData = await procurementResponse.json();
                if (procurementData.success && Array.isArray(procurementData.documents)) {
                  const procurementDocuments: SupportingDocument[] = procurementData.documents.map((doc: ProcurementDocumentResponse) => ({
                    id: doc.id,
                    file_name: doc.file_name || 'Procurement Document',
                    file_url: doc.file_path,
                    file_type: doc.file_type || 'image/jpeg',
                    file_size: doc.file_size || 0,
                    uploaded_at: doc.created_at,
                    document_type: (doc.metadata?.document_type as SupportingDocument['document_type']) || 'delivery_note',
                    source: (doc.metadata?.source as SupportingDocument['source']) || 'field_worker',
                    field_worker_id: doc.uploaded_by,
                    field_worker_name: doc.uploaded_by_name,
                    linked_purchase_order_id: doc.metadata?.linked_purchase_order_id,
                    linked_purchase_order_number: doc.metadata?.linked_purchase_order_number,
                    is_partial_delivery: doc.metadata?.is_partial_delivery,
                    delivery_status: doc.metadata?.delivery_status,
                    ai_validated: doc.metadata?.ai_validated,
                    ai_confidence: doc.metadata?.ai_confidence,
                  }));
                  loadedDocuments = [...loadedDocuments, ...procurementDocuments];
                }
              }
            } catch (procurementError) {
              console.error('[SupportingDocumentsModal] Failed to load procurement documents:', procurementError);
              // Don't fail the entire load if procurement documents fail
            }
          }
        }
      }

      setDocuments(loadedDocuments);
    } catch (error) {
      console.error('[SupportingDocumentsModal] Failed to load documents:', error);
      notify.error('Failed to load supporting documents');
    } finally {
      setLoading(false);
    }
  }, [workItemId, getAuthToken]);

  const handleCameraCapture = async (base64: string) => {
    try {
      // Convert base64 to File
      const response = await fetch(base64);
      const blob = await response.blob();
      
      // Create a File-like object
      const file = blob as File;
      Object.defineProperty(file, 'name', {
        value: `camera_${Date.now()}.jpg`,
        writable: false
      });
      
      setUploadFiles([file]);
      setActiveView('upload');
    } catch (error) {
      console.error('[SupportingDocumentsModal] Camera capture error:', error);
      notify.error('Failed to process camera capture');
    }
  };
  
  // Camera state
  const camera = useCamera({
    initialFacingMode: 'environment',
    quality: 0.85,
    onCapture: (base64: string) => {
      handleCameraCapture(base64);
    },
  });

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Load documents when modal opens
  useEffect(() => {
    if (isOpen && workItemId) {
      void loadDocuments();
    }
  }, [isOpen, workItemId, loadDocuments]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setActiveView('gallery');
        setDocuments([]);
        setSelectedDocument(null);
        setUploadFiles([]);
        setUploading(false);
      }, 300);
    }
  }, [isOpen]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadFiles(Array.from(files));
    setActiveView('upload');
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;

    try {
      setUploading(true);
      const token = getAuthToken();
      if (!token) throw new Error('No authentication token');

      const formData = new FormData();
      uploadFiles.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('work_item_id', workItemId);

      const response = await fetch('/api/v1/work-items/attachments/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          notify.success('Documents uploaded successfully');
          setUploadFiles([]);
          setActiveView('gallery');
          await loadDocuments();
        } else {
          throw new Error(data.error || 'Upload failed');
        }
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('[SupportingDocumentsModal] Upload error:', error);
      notify.error('Failed to upload documents');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const confirmed = await confirm({
        title: 'Delete Document',
        message: 'Are you sure you want to delete this document?',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      });
      if (!confirmed) return;

      const response = await fetch(`/api/v1/work-items/attachments/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        notify.success('Document deleted successfully');
        await loadDocuments();
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('[SupportingDocumentsModal] Delete error:', error);
      notify.error('Failed to delete document');
    }
  };

  const getDocumentIcon = (document: SupportingDocument) => {
    if (document.file_type.startsWith('image/')) {
      return ImageIcon;
    }
    if (document.file_type === 'application/pdf') {
      return FileText;
    }
    return File;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handlePhotoClick = (photo: SupportingDocument) => setSelectedPhoto(photo);

  const handleNextPhoto = () => {
    if (!selectedPhoto) return;
    const idx = documents.findIndex(d => d.id === selectedPhoto.id);
    if (idx < documents.length - 1) setSelectedPhoto(documents[idx + 1]);
  };

  const handlePreviousPhoto = () => {
    if (!selectedPhoto) return;
    const idx = documents.findIndex(d => d.id === selectedPhoto.id);
    if (idx > 0) setSelectedPhoto(documents[idx - 1]);
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 ${SUPPORTING_DOCUMENTS_OVERLAY_Z_CLASS} bg-gray-50 dark:bg-gray-900 overflow-y-auto`} style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="px-2 pb-1 pt-2 sm:px-4 sm:pb-3 sm:pt-4">
        {/* Header */}
        <div className="mb-3 sm:mb-6 px-2 relative">
          <div className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16">
              <FolderOpen className="h-6 w-6 sm:h-8 sm:w-8" />
            </div>
            <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1 text-gray-900 dark:text-white">Supporting Documents</h1>
          </div>
          <button
            onClick={onClose}
            className="absolute right-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* View Mode Toggle */}
        <div className="mb-4 px-2">
          <div className="flex items-center justify-center gap-2 bg-white dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveView('gallery')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${activeView === 'gallery'
                ? 'bg-gradient-to-br from-green-500 to-green-600 text-white'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              <FolderOpen className="w-4 h-4" />
              Gallery
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${activeView === 'upload'
                ? 'bg-gradient-to-br from-green-500 to-green-600 text-white'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
            {isCapacitor() && (
              <button
                onClick={() => camera.open()}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${activeView === 'upload'
                  ? 'bg-gradient-to-br from-green-500 to-green-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                <Camera className="w-4 h-4" />
                Camera
              </button>
            )}
          </div>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Gallery View */}
        {activeView === 'gallery' && (
          <div className="px-2" data-tour-id="receipt-photos-gallery">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12">
                <FolderOpen className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <p className="text-gray-500 dark:text-gray-400 mb-4">No supporting documents yet</p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Upload First Document
                </button>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {documents.map((doc) => {
                  const isImage = doc.file_type.startsWith('image/');
                  const Icon = getDocumentIcon(doc);
                  return (
                    <div
                      key={doc.id}
                      onClick={() => isImage ? handlePhotoClick(doc) : window.open(doc.file_url, '_blank')}
                      className={`group relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all hover:shadow-lg ${isDark
                        ? 'border-gray-700 hover:border-blue-500 bg-gray-800'
                        : 'border-gray-200 hover:border-blue-400 bg-gray-100'
                        }`}
                    >
                      {isImage ? (
                        <>
                          <img
                            src={doc.file_url}
                            alt={doc.file_name}
                            className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                          {/* Zoom overlay */}
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:bg-black/30 transition-all pointer-events-none">
                            <ZoomIn className="w-8 h-8 text-white" />
                          </div>
                        </>
                      ) : (
                        <div className="p-4">
                          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'} mb-3`}>
                            <Icon className={`w-8 h-8 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} />
                          </div>
                          <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {doc.file_name}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {formatFileSize(doc.file_size)}
                          </p>
                        </div>
                      )}

                      {/* AI Validated Badge */}
                      {doc.ai_validated && (
                        <div className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-bold bg-green-500/90 text-white z-10 backdrop-blur-sm">
                          <CheckCircle2 className="w-3 h-3 inline mr-1" />
                          AI
                        </div>
                      )}

                      {/* Delete button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDocument(doc.id);
                        }}
                        className="absolute bottom-2 right-2 p-1.5 rounded-full bg-red-600/90 hover:bg-red-700 text-white z-20 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Upload View */}
        {activeView === 'upload' && (
          <div className="px-2">
            <div className={`rounded-lg border p-6 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Upload Documents
              </h3>
              
              {uploadFiles.length > 0 && (
                <div className="mb-4 space-y-2">
                  {uploadFiles.map((file, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}
                    >
                      <FileText className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {file.name}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                      <button
                        onClick={() => setUploadFiles(prev => prev.filter((_, i) => i !== index))}
                        className={`p-1.5 rounded-md transition-colors ${isDark
                          ? 'hover:bg-gray-600 text-gray-400 hover:text-white'
                          : 'hover:bg-gray-200 text-gray-400 hover:text-gray-900'
                          }`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setUploadFiles([]);
                    setActiveView('gallery');
                  }}
                  className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${isDark
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploadFiles.length === 0 || uploading}
                  className={`flex-1 px-4 py-2 rounded-lg bg-green-600 text-white transition-colors hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload {uploadFiles.length} File{uploadFiles.length !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Camera Modal */}
        <SimpleCameraModal camera={camera} />

        {/* Photo Viewer */}
        {selectedPhoto && (
          <PhotoViewer
            photo={selectedPhoto}
            onClose={() => setSelectedPhoto(null)}
            onNext={handleNextPhoto}
            onPrevious={handlePreviousPhoto}
            onDelete={handleDeleteDocument}
          />
        )}
      </div>
    </div>
  );
}